import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Mail, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { COMPANY } from "../../data/products";

const LINKS = [
  { to: "/products/palativa", label: "PALATIVA", sub: "Ruminants" },
  { to: "/products/optibite", label: "OPTIBITE", sub: "Aqua" },
  { to: "/products/avitiva", label: "AVITIVA", sub: "Poultry" },
  { to: "/#science", label: "Science" },
  { to: "/#company", label: "About us" },
  { to: "/news", label: "News" },
  { to: "/contact", label: "Contact" },
];

const TopBar = ({ hidden }) => (
  <div className={`bg-[var(--forest)] text-white overflow-hidden transition-[max-height] duration-500 ${hidden ? "max-h-0" : "max-h-10"}`}>
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-10 flex items-center justify-between text-[13px]">
      <span className="hidden sm:inline text-[#CFE6D6]">Feed palatability · Aquaculture · Poultry performance</span>
      <div className="flex items-center gap-5 ml-auto">
        <a href={COMPANY.phoneHref} data-testid="topbar-phone" className="inline-flex items-center gap-1.5 hover:text-[var(--lime)]"><Phone size={13} />{COMPANY.phone}</a>
        <a href={`mailto:${COMPANY.email}`} data-testid="topbar-email" className="hidden sm:inline-flex items-center gap-1.5 hover:text-[var(--lime)]"><Mail size={13} />{COMPANY.email}</a>
      </div>
    </div>
  </div>
);

export const NavCorp = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header data-testid="site-header" className="fixed top-0 inset-x-0 z-50">
      <TopBar hidden={scrolled} />
      <div className={`bg-white border-b border-[var(--line)] transition-shadow duration-300 ${scrolled ? "shadow-[0_8px_30px_-18px_rgba(0,40,26,0.35)]" : ""}`}>
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-[76px] flex items-center justify-between gap-6">
          <Link to="/" data-testid="nav-logo-link"><Logo /></Link>
          <nav className="hidden xl:flex items-center h-full">
            {LINKS.map((l) => {
              const active = pathname === l.to;
              return (
                <Link key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`} className={`relative h-full flex items-center px-4 text-[15px] font-semibold transition-colors ${active ? "text-[var(--moss)]" : "text-[var(--ink)] hover:text-[var(--moss)]"}`}>
                  {l.label}
                  {l.sub && <span className="ml-1.5 text-[11px] font-medium text-[var(--muted)]">{l.sub}</span>}
                  <span className={`absolute left-4 right-4 bottom-0 h-[3px] bg-[var(--lime)] origin-left transition-transform duration-300 ${active ? "scale-x-100" : "scale-x-0"}`} />
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/contact" data-testid="nav-quote-button" className="hidden sm:inline-flex items-center gap-2 bg-[var(--moss)] hover:bg-[var(--forest)] text-white px-5 py-3 text-sm font-bold rounded-md transition-colors">
              Request a quote <ArrowRight size={16} />
            </Link>
            <button data-testid="nav-mobile-toggle" aria-label="Menu" onClick={() => setOpen(!open)} className="xl:hidden h-11 w-11 grid place-items-center rounded-md border border-[var(--line)]">
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav data-testid="nav-mobile-menu" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="xl:hidden bg-white border-b border-[var(--line)] shadow-lg">
            <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-3">
              {LINKS.map((l) => (
                <Link key={l.to} to={l.to} className="flex justify-between items-center py-3.5 border-b border-[var(--line)] last:border-0 font-display text-lg text-[var(--forest)]">
                  <span>{l.label}{l.sub && <span className="ml-2 text-xs font-sans font-medium text-[var(--muted)]">{l.sub}</span>}</span>
                  <ArrowRight size={16} className="text-[var(--leaf)]" />
                </Link>
              ))}
              <Link to="/contact" className="mt-3 mb-2 flex justify-center bg-[var(--moss)] text-white py-3.5 rounded-md font-bold sm:hidden">Request a quote</Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
